// src/pages/auth/Setup.jsx
// Página temporária de inicialização do banco de dados
// Acesse em /setup — remova a rota após o primeiro uso em produção

import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  getDoc,
  serverTimestamp,
} from 'firebase/firestore';

const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};

function getFirebaseInstances() {
  const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
  return { auth: getAuth(app), db: getFirestore(app) };
}

const ADMIN_EMAIL    = 'admin@agendapro.com';
const ADMIN_PASSWORD = 'Admin@123';

const PROCEDURES_SEED = [
  { name: 'Corte Feminino',    duration: 60,  price: 80,  emoji: '✂️', category: 'Cabelo',    approved: true },
  { name: 'Corte Masculino',   duration: 30,  price: 50,  emoji: '💈', category: 'Cabelo',    approved: true },
  { name: 'Escova Progressiva',duration: 180, price: 250, emoji: '💆', category: 'Cabelo',    approved: true },
  { name: 'Coloração',         duration: 120, price: 180, emoji: '🎨', category: 'Cabelo',    approved: true },
  { name: 'Manicure',          duration: 45,  price: 40,  emoji: '💅', category: 'Unhas',     approved: true },
  { name: 'Pedicure',          duration: 60,  price: 50,  emoji: '🦶', category: 'Unhas',     approved: true },
  { name: 'Limpeza de Pele',   duration: 60,  price: 120, emoji: '🧖', category: 'Estética',  approved: true },
  { name: 'Massagem Relaxante',duration: 60,  price: 150, emoji: '💆', category: 'Bem-estar', approved: true },
];

function EmailTest() {
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const test = async () => {
    setLoading(true);
    setStatus('');
    const serviceId  = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_CONFIRMATION;
    const publicKey  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      setStatus(`❌ Variáveis não carregadas!\nSERVICE_ID: ${serviceId || 'vazio'}\nTEMPLATE: ${templateId || 'vazio'}\nPUBLIC_KEY: ${publicKey ? '✓' : 'vazio'}`);
      setLoading(false);
      return;
    }

    try {
      const res = await emailjs.send(serviceId, templateId, {
        name:    'Teste AgendaPro',
        time:    '22/05/2026 às 10h00',
        message: '✅ Agendamento confirmado!\n\nProcedimento: Manicure\nProfissional: Maria\nData: 22/05/2026 às 10h00',
        to_email: 'melissalarangeira@gmail.com',
      }, publicKey);
      setStatus(`✅ E-mail enviado! Status: ${res.status} — ${res.text}`);
    } catch (err) {
      setStatus(`❌ Erro: ${err?.text || err?.message || JSON.stringify(err)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid #e5e2da' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#6B6070', marginBottom: 10 }}>
        🧪 Teste de E-mail (EmailJS)
      </div>
      <button onClick={test} disabled={loading} style={{
        width: '100%', padding: '12px', borderRadius: 10, border: 'none',
        background: loading ? '#e5e2da' : '#6B5876', color: '#fff',
        fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
      }}>
        {loading ? 'Enviando...' : '📧 Enviar e-mail de teste'}
      </button>
      {status && (
        <pre style={{
          marginTop: 12, padding: 12, background: '#1a1a2e', color: status.startsWith('✅') ? '#a8ffb0' : '#ff6b6b',
          borderRadius: 8, fontSize: 12, whiteSpace: 'pre-wrap', fontFamily: 'monospace',
        }}>{status}</pre>
      )}
    </div>
  );
}

// ── Vincular todos os profissionais a todos os procedimentos ──
function LinkAllProfessionals() {
  const [log, setLog]         = useState([]);
  const [running, setRunning] = useState(false);

  const addLog = (msg, ok = true) =>
    setLog(prev => [...prev, { msg, ok }]);

  const run = async () => {
    setRunning(true);
    setLog([]);
    try {
      const { db, auth } = getFirebaseInstances();
      if (!auth.currentUser) {
        addLog('⚠️  Faça login como admin antes de usar esta função.', false);
        return;
      }

      addLog('Buscando profissionais...');
      const prosSnap = await getDocs(
        query(collection(db, 'usuarios'), where('role', '==', 'professional'))
      );
      const pros = prosSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      addLog(`${pros.length} profissional(is) encontrado(s): ${pros.map(p => p.name).join(', ')}`);

      if (pros.length === 0) {
        addLog('⚠️  Nenhum profissional cadastrado. Crie profissionais em /admin/professionals primeiro.', false);
        return;
      }

      addLog('Buscando procedimentos...');
      const procsSnap = await getDocs(collection(db, 'procedimentos'));
      const procs     = procsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      addLog(`${procs.length} procedimento(s) encontrado(s)`);

      const proIds = pros.map(p => p.id);
      let updated  = 0;
      for (const proc of procs) {
        await doc(db, 'procedimentos', proc.id); // referência
        await import('firebase/firestore').then(({ updateDoc, doc: fsDoc }) =>
          updateDoc(fsDoc(db, 'procedimentos', proc.id), {
            professionals:  proIds,
            professionalId: proIds[0] || null,
            approved:       true,
          })
        );
        addLog(`  ✔ "${proc.name}" → vinculado a ${pros.length} profissional(is)`);
        updated++;
      }

      addLog('');
      addLog(`✅ ${updated} procedimento(s) atualizados! Agora clientes já conseguem agendar.`, true);
    } catch (e) {
      addLog(`❌ Erro: ${e?.message || e}`, false);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid #e5e2da' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#6B6070', marginBottom: 6 }}>
        🔗 Vincular profissionais aos procedimentos
      </div>
      <p style={{ fontSize: 12, color: '#a8a49d', marginBottom: 12, lineHeight: 1.6 }}>
        Conecta <strong>todos os profissionais</strong> cadastrados a <strong>todos os procedimentos</strong>
        — necessário para o calendário de agendamento funcionar.<br />
        Use apenas depois de criar os profissionais em <code>/admin/professionals</code>.
      </p>
      <button onClick={run} disabled={running} style={{
        width: '100%', padding: '12px', borderRadius: 10, border: 'none',
        background: running ? '#e5e2da' : '#6B5876', color: '#fff',
        fontSize: 14, fontWeight: 600, cursor: running ? 'not-allowed' : 'pointer',
      }}>
        {running ? 'Processando...' : '🔗 Vincular todos os profissionais'}
      </button>
      {log.length > 0 && (
        <pre style={{
          marginTop: 12, padding: 12, background: '#1a1a2e', color: '#a8ffb0',
          borderRadius: 8, fontSize: 11, whiteSpace: 'pre-wrap', fontFamily: 'monospace',
          maxHeight: 240, overflowY: 'auto',
        }}>
          {log.map((l, i) => (
            <span key={i} style={{ color: l.ok ? '#a8ffb0' : '#ff6b6b', display: 'block' }}>
              {l.msg}
            </span>
          ))}
        </pre>
      )}
    </div>
  );
}

// ── Deduplication component ─────────────────────────────────
function DeduplicateProcedures() {
  const [log, setLog]       = useState([]);
  const [running, setRunning] = useState(false);

  const addLog = (msg, ok = true) =>
    setLog(prev => [...prev, { msg, ok }]);

  const run = async () => {
    setRunning(true);
    setLog([]);
    try {
      const { db } = getFirebaseInstances();
      addLog('Lendo todos os procedimentos...');
      const snap = await getDocs(collection(db, 'procedimentos'));
      const all  = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      addLog(`Total encontrado: ${all.length} documentos`);

      // Agrupar por nome (case-insensitive)
      const groups = {};
      for (const p of all) {
        const key = (p.name || '').toLowerCase().trim();
        if (!groups[key]) groups[key] = [];
        groups[key].push(p);
      }

      let deleted = 0;
      for (const [, docs] of Object.entries(groups)) {
        if (docs.length <= 1) continue;
        addLog(`"${docs[0].name}": ${docs.length} cópias — excluindo ${docs.length - 1}...`);
        // Mantém o primeiro (mais antigo por inserção), deleta os demais
        for (let i = 1; i < docs.length; i++) {
          await deleteDoc(doc(db, 'procedimentos', docs[i].id));
          deleted++;
        }
      }

      if (deleted === 0) {
        addLog('Nenhum duplicado encontrado! 🎉');
      } else {
        addLog(`✅ ${deleted} duplicado(s) removido(s) com sucesso!`);
      }
    } catch (e) {
      addLog(`❌ Erro: ${e.message}`, false);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid #e5e2da' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#6B6070', marginBottom: 10 }}>
        🧹 Deduplicar Procedimentos
      </div>
      <p style={{ fontSize: 12, color: '#a8a49d', marginBottom: 12 }}>
        Remove procedimentos com o mesmo nome do Firestore. Roda apenas uma vez.
      </p>
      <button onClick={run} disabled={running} style={{
        width: '100%', padding: '12px', borderRadius: 10, border: 'none',
        background: running ? '#e5e2da' : '#16a34a', color: '#fff',
        fontSize: 14, fontWeight: 600, cursor: running ? 'not-allowed' : 'pointer',
      }}>
        {running ? 'Processando...' : '🧹 Remover Duplicados'}
      </button>
      {log.length > 0 && (
        <pre style={{
          marginTop: 12, padding: 12, background: '#1a1a2e', color: '#a8ffb0',
          borderRadius: 8, fontSize: 11, whiteSpace: 'pre-wrap', fontFamily: 'monospace',
          maxHeight: 200, overflowY: 'auto',
        }}>
          {log.map((l, i) => (
            <span key={i} style={{ color: l.ok ? '#a8ffb0' : '#ff6b6b', display: 'block' }}>
              {l.msg}
            </span>
          ))}
        </pre>
      )}
    </div>
  );
}

export default function Setup() {
  const [log, setLog]       = useState([]);
  const [running, setRunning] = useState(false);
  const [done, setDone]     = useState(false);
  const [error, setError]   = useState('');

  const addLog = (msg, ok = true) =>
    setLog(prev => [...prev, { msg, ok }]);

  const run = async () => {
    setRunning(true);
    setLog([]);
    setError('');
    try {
      const { auth, db } = getFirebaseInstances();

      // 1. Criar ou autenticar admin no Firebase Auth (UID real obrigatório para as rules)
      addLog('Criando/autenticando usuário administrador no Firebase Auth...');
      let uid;
      try {
        const cred = await createUserWithEmailAndPassword(auth, ADMIN_EMAIL, ADMIN_PASSWORD);
        uid = cred.user.uid;
        addLog(`Admin criado no Auth: ${ADMIN_EMAIL}`);
      } catch (e) {
        if (e.code === 'auth/email-already-in-use') {
          addLog('Admin já existe no Auth — autenticando com credenciais padrão...');
          const cred = await signInWithEmailAndPassword(auth, ADMIN_EMAIL, ADMIN_PASSWORD);
          uid = cred.user.uid;
          addLog(`Autenticado. UID: ${uid}`);
        } else {
          throw e;
        }
      }

      if (!auth.currentUser || auth.currentUser.uid !== uid) {
        throw new Error('Falha ao autenticar admin. Verifique e-mail/senha padrão.');
      }
      addLog(`Auth e-mail: ${auth.currentUser.email || '(sem e-mail no token)'}`);

      // 2. Salvar perfil no Firestore (coleção usuarios) — isOwner(userId) exige UID autenticado
      addLog('Salvando perfil do admin no Firestore (coleção: usuarios)...');
      try {
        await setDoc(doc(db, 'usuarios', uid), {
          uid,
          name:        'Administrador',
          email:       ADMIN_EMAIL,
          role:        'clinicAdmin',
          phone:       '',
          specialty:   '',
          active:      true,
          avatarColor: 'red',
          createdAt:   serverTimestamp(),
        }, { merge: true });
        addLog('Perfil admin salvo com sucesso.');
      } catch (e) {
        throw Object.assign(new Error(`Falha ao salvar perfil admin: ${e.message}`), { code: e.code, step: 'profile' });
      }

      const profileSnap = await getDoc(doc(db, 'usuarios', uid));
      const savedRole = profileSnap.data()?.role;
      addLog(`Role no Firestore: ${savedRole || '(vazio)'}`);

      // 2b. Configuração padrão do negócio
      addLog('Salvando configurações padrão do estabelecimento...');
      try {
        const configRef = doc(db, 'configuracoes', 'negocio');
        const configSnap = await getDoc(configRef);
        const configData = {
          name:      'AgendaPro',
          workDays:  [1, 2, 3, 4, 5, 6],
          workStart: '08:00',
          workEnd:   '18:00',
        };
        if (configSnap.exists()) {
          await setDoc(configRef, configData, { merge: true });
        } else {
          await setDoc(configRef, configData);
        }
        addLog('Configurações salvas.');
      } catch (e) {
        throw Object.assign(new Error(`Falha ao salvar configurações: ${e.message}`), { code: e.code, step: 'config' });
      }

      // 3. Criar procedimentos padrão (coleção procedimentos) — sem duplicar
      addLog('Criando procedimentos padrão (coleção: procedimentos)...');
      for (const proc of PROCEDURES_SEED) {
        try {
          const existing = await getDocs(query(collection(db, 'procedimentos'), where('name', '==', proc.name)));
          if (!existing.empty) {
            addLog(`  ⏭ ${proc.name} (já existe — pulando)`);
            continue;
          }
          await addDoc(collection(db, 'procedimentos'), {
            ...proc,
            professionals: [],
            createdAt: serverTimestamp(),
          });
          addLog(`  ✔ ${proc.name}`);
        } catch (e) {
          throw Object.assign(new Error(`Falha ao criar procedimento "${proc.name}": ${e.message}`), { code: e.code, step: 'procedures' });
        }
      }

      // 4. Criar coleções vazias com documento placeholder (para o Firestore reconhecer)
      // appointments, notifications, reschedule, scheduleWindows
      // Não precisam de seed — são criadas automaticamente no primeiro uso

      addLog('');
      addLog('✅ Banco de dados inicializado com sucesso!', true);
      addLog(`🔑 Login admin: ${ADMIN_EMAIL}  /  Senha: ${ADMIN_PASSWORD}`, true);
      addLog('⚠️  Altere a senha do admin após o primeiro login.', true);
      await signOut(auth);
      addLog('Sessão encerrada — faça login em /login.', true);
      setDone(true);
    } catch (e) {
      const stepHint = e.step ? ` [etapa: ${e.step}]` : '';
      const msg = e?.code === 'permission-denied'
        ? `Permissão negada no Firestore${stepHint}. Publique as regras: npx firebase-tools deploy --only firestore:rules`
        : (e.message || 'Erro desconhecido');
      setError(msg);
      addLog(`❌ Erro: ${msg}`, false);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#f8f7f5', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 24,
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 40,
        width: '100%', maxWidth: 560,
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        {/* Header */}
        <div style={{ marginBottom: 32, textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16, background: '#fef0f3',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 28, margin: '0 auto 16px',
          }}>🗄️</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a', margin: 0 }}>
            Inicializar Banco de Dados
          </h1>
          <p style={{ color: '#a8a49d', fontSize: 14, marginTop: 8 }}>
            Cria o usuário admin e as coleções no Firestore
          </p>
        </div>

        {/* Aviso: publicar regras antes do setup */}
        <div style={{
          background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 10,
          padding: '12px 16px', marginBottom: 24, fontSize: 13, color: '#1e40af', lineHeight: 1.6,
        }}>
          <strong>Antes de iniciar:</strong> publique as regras do Firestore.<br />
          No terminal (PowerShell/CMD):<br />
          <code style={{ display: 'block', marginTop: 6, background: '#1a1a2e', color: '#a8ffb0', padding: 8, borderRadius: 6, fontSize: 11 }}>
            npx firebase-tools login<br />
            npx firebase-tools deploy --only firestore:rules
          </code>
          <a href="https://console.firebase.google.com/project/sistemaagenda-78dc2/firestore/rules" target="_blank" rel="noreferrer"
            style={{ display: 'inline-block', marginTop: 8, color: '#1d4ed8', fontWeight: 600 }}>
            Ou cole as regras manualmente no Firebase Console →
          </a>
        </div>

        {/* Info box */}
        <div style={{
          background: '#fef9ec', border: '1px solid #f5d66a', borderRadius: 10,
          padding: '12px 16px', marginBottom: 24, fontSize: 13, color: '#7a6100',
        }}>
          <strong>O que será criado:</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, lineHeight: 1.7 }}>
            <li>Usuário admin: <code>{ADMIN_EMAIL}</code> / senha: <code>{ADMIN_PASSWORD}</code></li>
            <li>Coleção <code>usuarios</code> com o perfil do admin</li>
            <li>Coleção <code>procedimentos</code> com 8 procedimentos padrão</li>
            <li>Configurações em <code>configuracoes/negocio</code></li>
          </ul>
        </div>

        {/* Log output */}
        {log.length > 0 && (
          <div style={{
            background: '#1a1a2e', borderRadius: 10, padding: 16, marginBottom: 24,
            maxHeight: 240, overflowY: 'auto', fontSize: 12, lineHeight: 1.7,
          }}>
            {log.map((l, i) => (
              <div key={i} style={{ color: l.ok ? '#a8ffb0' : '#ff6b6b', fontFamily: 'monospace' }}>
                {l.msg}
              </div>
            ))}
            {running && (
              <div style={{ color: '#ffd93d', fontFamily: 'monospace' }}>
                ⏳ Aguarde...
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            background: '#fff0f0', border: '1px solid #fca5a5', borderRadius: 10,
            padding: '10px 14px', marginBottom: 20, color: '#b91c1c', fontSize: 13,
          }}>
            {error}
          </div>
        )}

        {/* Button */}
        {!done ? (
          <button
            onClick={run}
            disabled={running}
            style={{
              width: '100%', padding: '14px 0', borderRadius: 10, border: 'none',
              background: running ? '#e5e2da' : '#e94560', color: running ? '#a8a49d' : '#fff',
              fontSize: 15, fontWeight: 600, cursor: running ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
            }}
          >
            {running ? 'Inicializando...' : '🚀 Inicializar banco de dados'}
          </button>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10,
              padding: '16px', marginBottom: 20, color: '#166534', fontSize: 14,
            }}>
              <strong>Tudo pronto!</strong> Agora acesse{' '}
              <a href="/login" style={{ color: '#e94560', fontWeight: 600 }}>/login</a>{' '}
              e entre com o e-mail e senha do admin.
            </div>
            <a
              href="/login"
              style={{
                display: 'block', padding: '14px 0', borderRadius: 10,
                background: '#e94560', color: '#fff', textDecoration: 'none',
                fontSize: 15, fontWeight: 600, textAlign: 'center',
              }}
            >
              Ir para o Login →
            </a>
          </div>
        )}

        <LinkAllProfessionals />
        <DeduplicateProcedures />
        <EmailTest />

        <p style={{ textAlign: 'center', color: '#c5c0b8', fontSize: 11, marginTop: 20 }}>
          ⚠️ Esta página é temporária. Remova a rota <code>/setup</code> após usar.
        </p>
      </div>
    </div>
  );
}
